import * as React from "react";
import { HierarchyControl, HierarchyControlItem } from "../../../../basf-ui";
import { useDictionary, useParameter } from "../../../shared";
import { orderBy, first, sortBy } from "lodash";
import { dataSourcesService, dictionaryService } from "../../../dataFlow";
import { SLIDE_NUMBER } from "../constants";
import { equal } from "../../../../../shared";

interface ControlItem extends HierarchyControlItem<number> {
  olapId: number;
}

const BUSINESS_UNIT = 1;
export const CommoditiesControl: React.FC = () => {
  const [controlItems, setItems] = React.useState<ControlItem[]>([]);
  const [loadingValue, setLoadingValue] = React.useState<boolean>(true);
  const [selectedCommodityId, selectCommodityId] = useParameter<number>("@selected_commodity_id");
  const [selectedUnitId, selectUnitId] = useParameter<number>("@selected_unit_id");
  const [commodityIds, setCommodityIds] = useParameter<number[]>("@commodities");
  const [displayCommodityIds, setDisplayCommodityIds] = useParameter<number[]>("@commodities_display");

  const [dictItems] = useDictionary(dictionaryService, "slides-commodities-display", undefined, {
    business_unit: BUSINESS_UNIT,
    slide: SLIDE_NUMBER,
  });
  const [unitsDictItems, , unitsDictLoading] = useDictionary(dictionaryService, "f_units_ap_official_prices", undefined, {
    p_commodities: commodityIds?.join(","),
  });

  React.useEffect(
    function updateControlItems() {
      setLoadingValue(true);
      if (dictItems != null && unitsDictItems != null) {
        const sortedDictItems = orderBy(dictItems, (item) => item.getValue<number>("order"));
        const nextControlItems = sortedDictItems.map<ControlItem>((dictItem) => ({
          id: dictItem.getValue<number>("id")!,
          name: dictItem.getValue<Record<number, string>>("name")![9],
          parent: dictItem.getValue<number>("parent"),
          olapId: dictItem.getValue<number>("olap_commodity")!,
        }));
        setItems(nextControlItems);

        const selectedCandidates = nextControlItems.filter((e) => commodityIds?.includes(e.olapId));
        if (selectedCandidates) {
          const possibleIds = selectedCandidates.map((candidate) => candidate.id);
          if (!equal(possibleIds?.sort(), displayCommodityIds?.sort())) {
            setDisplayCommodityIds(possibleIds);
          }
        } else {
          setDisplayCommodityIds([nextControlItems[0]?.id]);
        }
        setLoadingValue(false);
      }
    },
    [dictItems, unitsDictItems]
  );

  const handlerChange = React.useCallback(
    async (value: number[]) => {
      if (!equal(displayCommodityIds, value)) {
        const selectedCandidates = controlItems.filter((e) => value.includes(e.id))!;
        setDisplayCommodityIds(value);

        const newCommodityIds = selectedCandidates.map((e) => e.olapId);
        const commodityIdsAndSources = await Promise.all(
          newCommodityIds.map(async (id) => {
            const source = await dataSourcesService.getDataSources(SLIDE_NUMBER, 1, Number(id));
            return {
              id,
              source,
            };
          })
        );
        const sortedCommodityIdsAndSources = sortBy(commodityIdsAndSources, (item) => item.source);
        setCommodityIds(sortedCommodityIdsAndSources.map((item) => item.id));

        if (selectedCommodityId != null && !newCommodityIds?.includes(selectedCommodityId)) {
          const commodityCandidate = first(newCommodityIds);

          selectCommodityId(Number(commodityCandidate));
          selectUnitId(Number(selectedUnitId));
        }
      }
    },
    [controlItems, displayCommodityIds, selectedCommodityId]
  );

  const label = "Commodities";
  return (
    <HierarchyControl
      noSelectableParent
      multiple
      large
      loading={unitsDictLoading}
      label={label}
      value={displayCommodityIds!}
      items={controlItems}
      onChange={handlerChange}
    />
  );
};
